import PropTypes from 'prop-types';
import { useMemo } from 'react';
import { css } from '@emotion/react';
import { Option } from '@veeva/corgix/select';
import SelectField from '../SelectField';
import { convertPhoneCodeToKey, formatPhoneCountryCode } from '../../services/countryUtils';
import getTargetAttribute from '../../services/getTargetAttribute';

const propTypes = {
    countries: PropTypes.arrayOf(
        PropTypes.exact({
            key: PropTypes.string,
            name: PropTypes.string,
            phoneCode: PropTypes.string,
        }),
    ),
    errorMessage: PropTypes.string,
    label: PropTypes.string,
    onChange: PropTypes.func.isRequired,
    /**
     * Country calling code (example: '1')
     */
    value: PropTypes.string,
};

const CountryCodeField = ({ countries, errorMessage, label, onChange, value }) => {
    const handleChange = (newValue) => {
        onChange(newValue?.replace('+', ''));
    };

    const countryCodeValue = useMemo(() => {
        const formattedPhoneCode = formatPhoneCountryCode(value);
        return {
            value: convertPhoneCodeToKey(formattedPhoneCode),
            label: formattedPhoneCode,
        };
    }, [value]);

    const options = useMemo(() => {
        return countries.map(({ key, phoneCode, name }) => (
            <Option value={phoneCode} keyValue={key} key={key} label={`${phoneCode} ${name}`}>
                <span
                    css={css`
                        margin-right: 8px;
                        font-weight: normal;
                    `}
                >
                    {phoneCode}
                </span>
                <span
                    css={({ grayLightest }) =>
                        css`
                            color: ${grayLightest};
                            font-weight: normal;
                        `
                    }
                >
                    {name}
                </span>
            </Option>
        ));
    }, [countries]);

    return (
        <SelectField
            onChange={handleChange}
            value={countryCodeValue}
            label={label}
            errorMessage={errorMessage}
            data-target={getTargetAttribute('country-code-field')}
        >
            {options}
        </SelectField>
    );
};

CountryCodeField.propTypes = propTypes;

export default CountryCodeField;
